import Logo from "assets/img/svg/navbar/logo";
import StyledButton from "components/shared/StyledButton";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import styles from "./style.module.scss";

type Status = "loading" | "success" | "error";

export const ValidateEmail: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [status, setStatus] = useState<Status>("loading");
    const [message, setMessage] = useState<string>(t("EmailValidation.CheckingLink"));

    const hasCalledApi = useRef(false);

    const token = searchParams.get("token");
    const idParam = searchParams.get("id");
    const lang = searchParams.get("lang");
    const step = searchParams.get("step");
    const newEmail = searchParams.get("newEmail")?.replace(/ /g, "+");

    useEffect(() => {
        if (lang) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    useEffect(() => {
        const processVerification = async () => {
            if (hasCalledApi.current) return;
            const beneficiaryId = idParam ? parseInt(idParam, 10) : null;

            if (!token || !beneficiaryId || isNaN(beneficiaryId) || !lang) {
                setStatus("error");
                setMessage(t("EmailValidation.InvalidLink"));
                return;
            }

            try {
                hasCalledApi.current = true;
                const endpoint = step === "new" ? "verify-new-email" : "verify-old-email";
                const BASE_URL = import.meta.env.REACT_APP_MAIL_UPDATE_API_ENDPOINT;
                const response = await fetch(`${BASE_URL}/${endpoint}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token, id: beneficiaryId, lang }),
                });

                if (!response.ok) {
                    setStatus("error");
                    setMessage(t("EmailValidation.InvalidLink"));
                    return;
                }

                setStatus("success");
                if (step === "new") {
                    setMessage(t("EmailValidation.SuccessNew"));
                } else {
                    setMessage(t("EmailValidation.SuccessOld", { email: newEmail }));
                }
            } catch (error: any) {
                hasCalledApi.current = false;
                setStatus("error");
                setMessage(t("EmailValidation.InvalidLink"));
            }
        };
        processVerification();
    }, [token, idParam, navigate, step, lang, t, newEmail]);

    return (
        <div className={styles.container}>
            <div className={styles.logo}>
                <Logo />
            </div>

            <div className={styles.card}>
                <h1 className={styles.title}>{t("EmailValidation.Title")}</h1>

                {status === "loading" && (
                    <div className={styles.statusContainer}>
                        <div className="spinner"></div>
                        <p className={styles.description}>{message}</p>
                    </div>
                )}

                {status === "success" && (
                    <div className={styles.statusContainer}>
                        <div className={styles.iconSuccess}>✓</div>
                        <p className={styles.descriptionSuccess}>{message}</p>
                        {step === "new" && (
                            <div className={styles.loginButton}>
                                <StyledButton buttonType="primary" onClick={() => (window.location.href = "/login")}>
                                    {t("EmailValidation.Login")}
                                </StyledButton>
                            </div>
                        )}
                    </div>
                )}

                {status === "error" && (
                    <div className={styles.statusContainer}>
                        <div className={styles.iconError}>✕</div>
                        <p className={styles.descriptionError}>{message}</p>
                    </div>
                )}
            </div>
        </div>
    );
};
